/* Which hydrophone, when a farm has more than one.
 *
 * Two boxes at one place is a legal and intended state -- the exclusion
 * constraints on `missions` are per compute and per microphone, never per site
 * -- and Ljosoy has run that way since August. Until now nothing in the
 * interface could name the second one: `active_mission_id` is a single value
 * chosen by `ORDER BY started_at DESC LIMIT 1`, and everything downstream
 * followed it.
 *
 * ---------------------------------------------------------------------------
 * THREE RULES, AND THE FIRST IS THE ONE THAT MATTERS
 *
 * **Switching keeps your position in time.** This is the dashcam pattern:
 * front camera and rear camera share one timeline, and changing channel asks
 * "what did the other one see *at this moment*", not "take me back to now".
 * Two hydrophones at one farm exist precisely so that a sound can be compared
 * between them, and a switch that reset the clock would make the comparison
 * impossible to perform.
 *
 * **One live at a time.** A live stream is not a view of something already
 * happening: pressing play wakes a node and makes it upload a segment every
 * second over a farm uplink. Two at once is two uplinks producing something
 * nobody can listen to, since two simultaneous audio streams are noise. The
 * page stops the old one when it starts the new one, and says so.
 *
 * **One sensor renders nothing.** A strip of one is a control with no choice
 * in it, and it would appear on almost every farm.
 *
 * ---------------------------------------------------------------------------
 * THE DOT
 *
 * Filled when the node has been heard from inside five minutes -- the rule
 * Home.tsx already uses, and deliberately not "the mission says active". A
 * mission can be active while its node has been silent for a week; the dot
 * would then be confidently wrong about the one thing anybody checks it for.
 */
import type { SiteMission } from '../api'
import { useT } from '../lang'

/** How recently a node must have spoken to count as here. Matches the farm
 *  card, so the same box is not live on one screen and quiet on another. */
const LIVE_WINDOW_MS = 5 * 60 * 1000

export function isLive(m: SiteMission): boolean {
  if (!m.last_seen_at) return false
  return Date.now() - new Date(m.last_seen_at).getTime() < LIVE_WINDOW_MS
}

/** What to call a hydrophone on screen.
 *
 *  `label` is what somebody typed -- "Merd 3", "Forflate" -- and is what a
 *  farmer means. It is null on all fourteen units in the fleet today, so the
 *  fallback is the path that actually runs, and the fallback used to be the
 *  model number off the case: two chips reading `H2dM15` and `A5iM15`. That is
 *  a technician's handle, and printing it as the primary name is most of what
 *  made this page read as an instrument rather than a product -- nobody on a
 *  farm knows which pen `A5iM15` is in.
 *
 *  Before the count, though, comes the name painted on the box. Ljosoy's two
 *  enclosures read `p-001` and `p-002`, and that is the one name that can be
 *  checked against something physical: a person at the mooring with a phone
 *  can confirm they are listening to the box in front of them. A counted
 *  fallback cannot be confirmed by anybody -- it is only ever our word for it.
 *
 *  So: what somebody typed, then what is on the box, then the count. Each rung
 *  is more specific than the one below it, none of them is invented, and all
 *  three are things a person can repeat down a phone. The model and serial stay
 *  on the chip as a second line, where a technician still finds them.
 */
export function sensorName(m: SiteMission, index: number, post: string): string {
  if (m.microphone_label) return m.microphone_label
  if (m.node_label) return m.node_label
  return `${post} ${index + 1}`
}

/** The model and serial, or nothing. Never the mission id: an `msn-` string is
 *  a fact about our database, and there is no question a farmer has that it
 *  answers. */
function sensorSub(m: SiteMission): string | null {
  const bits = [m.microphone_type, m.microphone_serial].filter(Boolean)
  return bits.length ? bits.join(' · ') : null
}

export function Sensors({
  missions, selected, onSelect,
}: {
  missions: SiteMission[]
  selected: string
  onSelect: (missionId: string) => void
}) {
  const t = useT()
  if (missions.length < 2) return null

  /* Oldest first: the order the hydrophones went in the water, which is stable.
   * Ordering by `last_seen_at` would reshuffle the chips whenever one of them
   * uploaded, and a control that moves under a thumb is a control that gets
   * mispressed on a boat. */
  const rows = [...missions].sort((a, b) => a.started_at.localeCompare(b.started_at))

  return (
    <div className="sensors" role="tablist" aria-label={t('Listening post')}>
      {rows.map((m, i) => {
        const live = isLive(m)
        const sub = sensorSub(m)
        return (
          <button
            key={m.mission_id}
            type="button"
            role="tab"
            aria-selected={m.mission_id === selected}
            className="sensor"
            onClick={() => onSelect(m.mission_id)}
            title={sub ?? undefined}
          >
            <span className={live ? 'pulse on' : 'pulse'} aria-hidden="true" />
            <span className="sensor-text">
              <span className="sensor-name">
                {sensorName(m, i, t('Listening post'))}
              </span>
              {sub && <span className="sensor-sub">{sub}</span>}
            </span>
            <span className="vh">
              {live ? t('listening now') : t('not heard from')}
            </span>
          </button>
        )
      })}
    </div>
  )
}
